import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../../store/AuthContext';
import { getNotificationCount } from '../../services/notificationService';
import NotificationsDropdown from './NotificationsDropdown';
import './NotificationBell.css';

// Global cache to prevent duplicate requests across all instances
const globalNotificationCache = {
  count: 0,
  promise: null as Promise<number> | null,
  lastFetch: 0,
  CACHE_DURATION: 30000, // 30 seconds
  intervalId: null as NodeJS.Timeout | null,
  isAuthenticated: false,
};

const NotificationBell: React.FC = () => {
  const { isAuthenticated } = useContext(AuthContext);

  const [unreadCount, setUnreadCount] = useState(globalNotificationCache.count);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Component-level refs to track mounting state
  const isMounted = useRef(true);
  const hasInitialized = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch notification count with global caching
  const fetchNotificationCount = useCallback(async () => {
    if (!isAuthenticated) {
      if (isMounted.current) {
        setUnreadCount(0);
      }
      return;
    }

    const now = Date.now();

    // Check if we have fresh cached data
    if (
      globalNotificationCache.count !== undefined &&
      now - globalNotificationCache.lastFetch < globalNotificationCache.CACHE_DURATION
    ) {
      if (isMounted.current) {
        setUnreadCount(globalNotificationCache.count);
      }
      return;
    }

    // Check if there's already a request in progress
    if (globalNotificationCache.promise !== null) {
      try {
        const count = await globalNotificationCache.promise;
        if (isMounted.current) {
          setUnreadCount(count);
        }
      } catch (err) {
        console.error('Error fetching notification count:', err);
      }
      return;
    }

    // Start new request
    if (isMounted.current) {
      setLoading(true);
    }

    globalNotificationCache.promise = getNotificationCount();

    try {
      const count = await globalNotificationCache.promise;
      globalNotificationCache.count = count;
      globalNotificationCache.lastFetch = now;

      if (isMounted.current) {
        setUnreadCount(count);
      }
    } catch (err) {
      console.error('Error fetching notification count:', err);
    } finally {
      globalNotificationCache.promise = null;
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [isAuthenticated]);

  // Setup interval for periodic fetching
  const setupInterval = useCallback(() => {
    // Clear existing interval
    if (globalNotificationCache.intervalId) {
      clearInterval(globalNotificationCache.intervalId);
      globalNotificationCache.intervalId = null;
    }

    if (isAuthenticated) {
      // Set up new interval
      globalNotificationCache.intervalId = setInterval(() => {
        // Only fetch if cache is stale
        const now = Date.now();
        if (now - globalNotificationCache.lastFetch >= globalNotificationCache.CACHE_DURATION) {
          fetchNotificationCount();
        }
      }, 60000); // Check every 60 seconds
    }
  }, [isAuthenticated, fetchNotificationCount]);

  // Handle authentication state changes
  useEffect(() => {
    // Reset cache when auth state changes
    if (globalNotificationCache.isAuthenticated !== isAuthenticated) {
      globalNotificationCache.isAuthenticated = isAuthenticated;
      globalNotificationCache.count = 0;
      globalNotificationCache.lastFetch = 0;
      hasInitialized.current = false;

      if (!isAuthenticated) {
        // Clear interval and data when not authenticated
        if (globalNotificationCache.intervalId) {
          clearInterval(globalNotificationCache.intervalId);
          globalNotificationCache.intervalId = null;
        }
        if (isMounted.current) {
          setUnreadCount(0);
        }
      }
    }
  }, [isAuthenticated]);

  // Initialize data fetching only once per component lifecycle
  useEffect(() => {
    if (hasInitialized.current || !isAuthenticated) return;
    hasInitialized.current = true;

    fetchNotificationCount();
    setupInterval();

    // Cleanup function
    return () => {
      if (globalNotificationCache.intervalId) {
        clearInterval(globalNotificationCache.intervalId);
        globalNotificationCache.intervalId = null;
      }
    };
  }, [isAuthenticated, fetchNotificationCount, setupInterval]);

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  // Close dropdown
  const closeDropdown = () => {
    setIsOpen(false);
  };

  // Handle notification read - invalidate cache to force refresh
  const handleNotificationRead = useCallback(() => {
    // Invalidate cache to force fresh data
    globalNotificationCache.lastFetch = 0;

    // Fetch fresh data if not already loading
    if (!loading && isMounted.current) {
      fetchNotificationCount();
    }
  }, [fetchNotificationCount, loading]);

  if (!isAuthenticated) return null;

  return (
    <div className="notification-bell-container">
      <button
        className="notification-bell-btn"
        onClick={toggleDropdown}
        aria-label="Notifications"
        disabled={loading}
      >
        <span className="bell-icon">{loading ? '⟳' : '🔔'}</span>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      <NotificationsDropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        unreadCount={unreadCount}
        onNotificationRead={handleNotificationRead}
      />
    </div>
  );
};

export default React.memo(NotificationBell);
